import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Company, CompanyMembership } from '../types/company';
import { useAuth } from './AuthProvider';
import { listMyMemberships, setActiveCompanyPreference, type Membership } from '../lib/identity';
import { AppError, toAppError, type AppErrorKind } from '../lib/errors';
import { setActiveCompanyId, clearActiveCompanyId } from '../lib/companyContext';
import { isPreviewMode, PREVIEW_COMPANIES, setActivePreviewCompany } from '../lib/preview/flag';
import { LoadingScreen } from '../components/feedback/LoadingScreen';
import { EmptyState, ErrorState, Button } from '../ui';
import { CompanyChooser } from '../components/company/CompanyChooser';

// Cold-start company context (ADMINISTRATION-DESIGN §4/§9 [C1], Slice 6C-1). Depends only on
// AuthProvider — never on IdentityProvider (that dependency ran the other way and created a
// cycle). Discovers the caller's own memberships header-independently
// (app.fn_list_my_memberships, via identity.ts), filters to `active` only (invited/inactive
// are never selectable), and resolves 0/1/N active memberships into an explicit state machine:
// `empty` (blocking, no app children), a single active membership auto-selected, several with
// one marked preferred (the last fn_set_active_company choice) auto-selected too (D-224), or
// `choosing` (chooser — several and none preferred, e.g. the very first sign-in). Every selection
// persists the preference (fn_set_active_company) before installing the tab-local dispatch-time
// context (companyContext.ts) that every subsequent request reads — never localStorage/
// sessionStorage/cookies. Blocks rendering its own `children` (and therefore IdentityProvider,
// mounted beneath it) until a company is installed, so no company-scoped request can fire
// before selection.
type CompanyStatus = 'loading' | 'choosing' | 'ready' | 'empty' | 'error' | 'revoked';

interface CompanyState {
  readonly status: CompanyStatus;
  readonly memberships: readonly CompanyMembership[];
  readonly activeCompany: Company | null;
  readonly errorMessage: string | null;
  readonly selecting: boolean;
  readonly setActiveCompany: (companyId: string) => void;
  readonly select: (companyId: string) => Promise<void>;
  readonly retry: () => void;
}

const CompanyContext = createContext<CompanyState | null>(null);

interface InternalState {
  readonly status: CompanyStatus;
  readonly choices: readonly Membership[];
  readonly activeCompanyId: string | null;
  readonly error: AppError | null;
  readonly selecting: boolean;
}

const INITIAL_STATE: InternalState = {
  status: 'loading',
  choices: [],
  activeCompanyId: null,
  error: null,
  selecting: false,
};

function previewChoices(): readonly Membership[] {
  return PREVIEW_COMPANIES.map((c, i) => ({
    companyId: c.id,
    companyName: c.name,
    membershipStatus: 'active',
    isPreferred: i === 0,
  }));
}

// Safe, presentation-only Georgian text per AppError kind — never render AppError.message/code
// or any backend/database text directly (it may carry raw SQL/RPC diagnostics).
const SAFE_ERROR_TEXT: Record<AppErrorKind, string> = {
  unauthorized: 'სესია ვადაგასულია. გთხოვთ, ხელახლა შეხვიდეთ სისტემაში.',
  forbidden: 'ამ კომპანიაზე წვდომა შეზღუდულია.',
  not_found: 'კომპანია ვერ მოიძებნა.',
  timeout: 'მოთხოვნას დასჭირდა ძალიან დიდი დრო. სცადეთ თავიდან.',
  too_large: 'მოთხოვნა ვერ დამუშავდა. სცადეთ თავიდან.',
  network: 'კავშირის შეცდომა. შეამოწმეთ ინტერნეტთან კავშირი და სცადეთ თავიდან.',
  invalid_payload: 'არჩეული კომპანია არასწორია. სცადეთ თავიდან.',
  stale_write: 'მონაცემები განახლდა. სცადეთ თავიდან.',
  idempotency_conflict: 'მოთხოვნა უკვე მუშავდება. სცადეთ თავიდან.',
  unknown: 'დაფიქსირდა შეცდომა. სცადეთ თავიდან.',
};

function presentCompanyError(error: AppError | null): string | null {
  return error === null ? null : SAFE_ERROR_TEXT[error.kind];
}

export function CompanyProvider({ children }: { children: ReactNode }): JSX.Element {
  const { status: authStatus, authUser, signOut } = useAuth();
  const authUserId = authUser?.authUserId ?? null;

  const [state, setState] = useState<InternalState>(INITIAL_STATE);
  const [retryToken, setRetryToken] = useState(0);
  const generationRef = useRef(0);
  // Per-generation selection lock (not a shared boolean): holds the generation number
  // currently dispatching a selection, or null. This lets a new authenticated-user generation
  // dispatch its own selection while an old generation's preference RPC is still pending, and
  // stops a late-resolving old generation's `finally` from clearing a newer generation's lock.
  const selectingGenerationRef = useRef<number | null>(null);
  const choicesRef = useRef<readonly Membership[]>([]);
  const activeCompanyIdRef = useRef<string | null>(null);

  useEffect(() => {
    choicesRef.current = state.choices;
  }, [state.choices]);

  useEffect(() => {
    activeCompanyIdRef.current = state.activeCompanyId;
  }, [state.activeCompanyId]);

  // Unmount must synchronously invalidate the current generation too — the discovery effect's
  // own `cancelled` flag is local to that effect and invisible to `applySelection`, and
  // generationRef is otherwise only bumped on auth change, not on unmount.
  useEffect(() => {
    return () => {
      generationRef.current += 1;
    };
  }, []);

  // Validates the choice against the latest discovered active set, persists the preference
  // first, and only then installs the tab-local context — on failure the caller's prior status
  // (e.g. `choosing`) is preserved so the chooser stays open with an inline error. `generation`
  // guards against a late response after auth change/unmount: every state/context mutation below
  // is gated on `generationRef.current === generation`, so a stale (superseded) generation can
  // never install a context or touch state, and a new generation is never blocked by an old
  // generation's still-pending selection — only a duplicate dispatch within the SAME generation
  // is deduped, via the per-generation `selectingGenerationRef` lock (never a plain boolean).
  const applySelection = useCallback(
    async (companyId: string, generation: number, activeChoices: readonly Membership[]): Promise<void> => {
      // Selecting the already-active company is a no-op: no preference RPC, no context
      // rotation, no remount.
      if (companyId === activeCompanyIdRef.current) return;
      if (selectingGenerationRef.current === generation) return;
      const found = activeChoices.find((c) => c.companyId === companyId);
      if (found === undefined) {
        if (generationRef.current === generation) {
          setState((prev) => ({
            ...prev,
            error: new AppError('invalid_payload', 'COMPANY_CHOICE_INVALID', 'COMPANY_CHOICE_INVALID'),
          }));
        }
        return;
      }

      selectingGenerationRef.current = generation;
      if (generationRef.current === generation) {
        setState((prev) => ({ ...prev, selecting: true, error: null }));
      }

      try {
        if (isPreviewMode()) {
          if (generationRef.current !== generation) return;
          setActivePreviewCompany(companyId);
        } else {
          await setActiveCompanyPreference(companyId);
        }
        if (generationRef.current !== generation) return;
        setActiveCompanyId(companyId);
        setState({
          status: 'ready',
          choices: activeChoices,
          activeCompanyId: companyId,
          error: null,
          selecting: false,
        });
      } catch (err) {
        if (generationRef.current !== generation) return;
        const appError = toAppError(err);
        if (appError.kind === 'forbidden' || appError.kind === 'not_found') {
          // Possible mid-session revocation (ADMINISTRATION-DESIGN §4: "revoked mid-session ...
          // forces re-selection or sign-out"). Clear the tab-local context immediately and
          // block the company-scoped subtree — never silently keep serving (or re-offering
          // from stale choices) a target that was just rejected.
          clearActiveCompanyId();
          setState({ status: 'revoked', choices: [], activeCompanyId: null, error: appError, selecting: false });
        } else {
          setState((prev) => ({
            ...prev,
            status: prev.status === 'choosing' ? 'choosing' : prev.status === 'ready' ? 'ready' : 'error',
            error: appError,
            selecting: false,
          }));
        }
      } finally {
        // Only clear the lock if it still belongs to this generation — a stale generation's
        // `finally` must never unlock (or otherwise mutate) a newer generation's operation.
        if (selectingGenerationRef.current === generation) {
          selectingGenerationRef.current = null;
        }
      }
    },
    [],
  );

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;

    // On any authenticated-user change (or auth loss), the tab-local context is stale.
    clearActiveCompanyId();

    if (authStatus !== 'authenticated' || authUserId === null) {
      setState(INITIAL_STATE);
      return;
    }

    setState({ status: 'loading', choices: [], activeCompanyId: null, error: null, selecting: false });
    let cancelled = false;

    void (async () => {
      try {
        const choices = isPreviewMode()
          ? previewChoices()
          : (await listMyMemberships()).filter((m) => m.membershipStatus === 'active');

        if (cancelled || generationRef.current !== generation) return;

        if (choices.length === 0) {
          setState({ status: 'empty', choices: [], activeCompanyId: null, error: null, selecting: false });
          return;
        }
        // One active membership, or several with a persisted preference (D-224): auto-select it
        // through the same applySelection — so fn_set_active_company still runs first and a
        // membership revoked meanwhile still lands in `revoked`. No preference → the chooser.
        const autoChoice = choices.length === 1 ? choices[0] : choices.find((c) => c.isPreferred);
        if (autoChoice !== undefined) {
          setState({ status: 'loading', choices, activeCompanyId: null, error: null, selecting: false });
          await applySelection(autoChoice.companyId, generation, choices);
          return;
        }
        setState({ status: 'choosing', choices, activeCompanyId: null, error: null, selecting: false });
      } catch (err) {
        if (cancelled || generationRef.current !== generation) return;
        setState({ status: 'error', choices: [], activeCompanyId: null, error: toAppError(err), selecting: false });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applySelection is stable (no deps)
  }, [authStatus, authUserId, retryToken]);

  const select = useCallback(
    (companyId: string): Promise<void> => applySelection(companyId, generationRef.current, choicesRef.current),
    [applySelection],
  );

  const setActiveCompany = useCallback(
    (companyId: string): void => {
      void select(companyId);
    },
    [select],
  );

  const retry = useCallback(() => {
    setRetryToken((t) => t + 1);
  }, []);

  const memberships = useMemo<readonly CompanyMembership[]>(
    () => state.choices.map((c) => ({ company: { id: c.companyId, name: c.companyName }, isDefault: c.isPreferred })),
    [state.choices],
  );

  const activeCompany = useMemo<Company | null>(() => {
    if (state.activeCompanyId === null) return null;
    const found = state.choices.find((c) => c.companyId === state.activeCompanyId);
    return found === undefined
      ? { id: state.activeCompanyId, name: state.activeCompanyId }
      : { id: found.companyId, name: found.companyName };
  }, [state.activeCompanyId, state.choices]);

  const errorMessage = useMemo(() => presentCompanyError(state.error), [state.error]);

  const value = useMemo<CompanyState>(
    () => ({
      status: state.status,
      memberships,
      activeCompany,
      errorMessage,
      selecting: state.selecting,
      setActiveCompany,
      select,
      retry,
    }),
    [state.status, memberships, activeCompany, errorMessage, state.selecting, setActiveCompany, select, retry],
  );

  let content: ReactNode = children;
  if (authStatus === 'authenticated') {
    switch (state.status) {
      case 'loading':
        content = <LoadingScreen label="კომპანიის დადგენა…" />;
        break;
      case 'choosing':
        content = (
          <CompanyChooser
            choices={state.choices}
            selecting={state.selecting}
            errorMessage={presentCompanyError(state.error)}
            onSelect={setActiveCompany}
          />
        );
        break;
      case 'empty':
        content = (
          <div className="flex h-full w-full items-center justify-center">
            <EmptyState
              title="კომპანიაზე წვდომა არ გაქვთ"
              description="თქვენი ანგარიშისთვის აქტიური წევრობა ვერ მოიძებნა. დაუკავშირდით ადმინისტრატორს."
              action={
                <Button variant="secondary" onClick={() => void signOut()}>
                  გამოსვლა
                </Button>
              }
            />
          </div>
        );
        break;
      case 'error':
        content = (
          <div className="flex h-full w-full items-center justify-center">
            <ErrorState
              title="კომპანიის ჩატვირთვა ვერ მოხერხდა"
              description={presentCompanyError(state.error) ?? 'უცნობი შეცდომა'}
              action={
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={retry}>
                    თავიდან ცდა
                  </Button>
                  <Button variant="ghost" onClick={() => void signOut()}>
                    გამოსვლა
                  </Button>
                </div>
              }
            />
          </div>
        );
        break;
      case 'revoked':
        content = (
          <div className="flex h-full w-full items-center justify-center">
            <ErrorState
              title="წვდომა კომპანიაზე განახლდა"
              description="შესაძლოა თქვენი წევრობა ამ კომპანიაში გაუქმდა. გთხოვთ, ხელახლა აირჩიოთ კომპანია."
              action={
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={retry}>
                    თავიდან ცდა
                  </Button>
                  <Button variant="ghost" onClick={() => void signOut()}>
                    გამოსვლა
                  </Button>
                </div>
              }
            />
          </div>
        );
        break;
      case 'ready':
      default:
        // Keyed on the active company so a switch fully remounts the company-scoped subtree
        // (component-local data/cursors/drawers/forms) — old-company UI never lingers after a
        // successful context installation.
        content = <Fragment key={state.activeCompanyId ?? 'none'}>{children}</Fragment>;
    }
  }

  return <CompanyContext.Provider value={value}>{content}</CompanyContext.Provider>;
}

export function useCompany(): CompanyState {
  const ctx = useContext(CompanyContext);
  if (ctx === null) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return ctx;
}
